import type { Container } from "pixi.js";
import type { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { PresentationTaskRunner } from "../runner/PresentationTaskRunner";
import { playClipAndWait, queueClip } from "./SpineClipPlayer";

/** What a panel's chrome lends the motion: the dim and the hint it fades, and its fades. The shell's `ModalChrome` is one. */
export interface PanelFadeSurface {
  readonly dim: Container;
  readonly continueTextRoot: Container;
  /** Fades `target.alpha` to `alpha` over `seconds`, cancelling a fade already running on it. */
  fade(target: Container, alpha: number, seconds: number): Promise<void>;
  stopFade(target: Container): void;
}

/**
 * The fades a Spine panel runs beside its clips (seconds of the runner the motion is given — the chrome runner for the
 * overlays). The clips own the enter, idle and exit motion; a title's values.
 */
export interface SpinePanelFadeTuning {
  /** The dim's fade in / out. */
  enterDurationSeconds: number;
  exitDurationSeconds: number;
  /** The "tap to continue" hint's fade in / out. */
  continueFadeInSeconds: number;
  continueFadeOutSeconds: number;
}

/**
 * A Spine-driven overlay panel's motion (the feature trigger and finished panels; decision 46): seated at its setup
 * pose with the dim and the hint at 0 before it shows, so no frame shows the previous loop or the end of `out`; on
 * enter `in` then the `idle` loop, the dim fading in beside it; on exit `out`, the dim and the hint fading out. Without
 * a skeleton nothing plays and the dim stays down. The panel keeps its art, its geometry and when it calls these.
 */
export class SpinePanelMotion {
  private readonly clips: { in: string; idle: string; out: string };
  private readonly track: number;

  public constructor(
    private readonly input: {
      surface: PanelFadeSurface;
      skeleton: Spine | null;
      runner: PresentationTaskRunner;
      tuning: SpinePanelFadeTuning;
      /** Default `in` / `idle` / `out`. */
      clips?: { in: string; idle: string; out: string };
      /** Default 0. */
      track?: number;
    },
  ) {
    this.clips = input.clips ?? { in: "in", idle: "idle", out: "out" };
    this.track = input.track ?? 0;
  }

  /** The entry state, seated before the panel shows. Idempotent. */
  public preSeat(): void {
    const sk = this.input.skeleton;
    if (sk) {
      sk.state.clearTracks();
      sk.skeleton.setToSetupPose();
      sk.update(0);
    }
    const { surface } = this.input;
    surface.stopFade(surface.continueTextRoot);
    surface.continueTextRoot.alpha = 0;
    surface.stopFade(surface.dim);
    surface.dim.alpha = 0;
  }

  /** `in`, then the `idle` loop, with the dim fading in; resolves when `in` and the fade are done. */
  public async enter(): Promise<void> {
    this.preSeat();
    const sk = this.input.skeleton;
    if (!sk) return;
    const inClip = playClipAndWait(sk, this.input.runner, this.clips.in, { track: this.track });
    queueClip(sk, this.clips.idle, true, this.track);
    await Promise.all([this.input.surface.fade(this.input.surface.dim, 1, this.input.tuning.enterDurationSeconds), inClip]);
  }

  /** `out`, with the dim and the hint fading out; resolves when all three are done. */
  public async exit(): Promise<void> {
    const { surface, tuning, skeleton } = this.input;
    const waits: Promise<unknown>[] = [];
    if (skeleton) waits.push(playClipAndWait(skeleton, this.input.runner, this.clips.out, { track: this.track }));
    waits.push(surface.fade(surface.dim, 0, tuning.exitDurationSeconds));
    waits.push(surface.fade(surface.continueTextRoot, 0, tuning.continueFadeOutSeconds));
    await Promise.all(waits);
  }

  /** The "tap to continue" hint, from 0. */
  public async fadeInContinueText(): Promise<void> {
    const { surface } = this.input;
    surface.stopFade(surface.continueTextRoot);
    surface.continueTextRoot.alpha = 0;
    await surface.fade(surface.continueTextRoot, 1, this.input.tuning.continueFadeInSeconds);
  }

  /** On hide: the clips cleared, the fades stopped where they are. */
  public stop(): void {
    this.input.skeleton?.state.clearTracks();
    const { surface } = this.input;
    surface.stopFade(surface.continueTextRoot);
    surface.stopFade(surface.dim);
  }
}
